/**
 * AgentsFlowScreen
 * Main entry point for the AgentsFlow module
 * Displays list of agents with options to create, edit, duplicate and delete
 */

import { useEffect, useCallback, useState, useMemo } from 'react';
import { Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  IconRobot,
  IconPlus,
  IconRefresh,
  IconEdit,
  IconTrash,
  IconSearch,
  IconCopy,
  IconAlertTriangle,
  IconBellRinging,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';

import { AgentsFlowProvider, useAgentsFlow } from './contexts/AgentsFlowContext';
import { useAgentsFlowApi } from './hooks/useAgentsFlowApi';
import { CAPABILITIES } from './constants';
import AgentsFlowService from './services/AgentsFlowService';

import './styles/agents-flow.css';
import './styles/agents-flow-screen.css';

// ===============================================
// LOADING SKELETON
// ===============================================

const AgentCardSkeleton = () => (
  <div className="afs-skeleton">
    <div style={{ padding: '1.125rem 1.125rem 0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="afs-skeleton__bar" style={{ width: 44, height: 44, borderRadius: 10 }} />
        <div style={{ flex: 1 }}>
          <div className="afs-skeleton__bar" style={{ width: '65%', height: 14, marginBottom: 6 }} />
          <div className="afs-skeleton__bar" style={{ width: '40%', height: 12 }} />
        </div>
      </div>
    </div>
    <div style={{ padding: '0 1.125rem 0.875rem' }}>
      <div style={{ display: 'flex', gap: '0.375rem' }}>
        <div className="afs-skeleton__bar" style={{ width: 72, height: 20 }} />
        <div className="afs-skeleton__bar" style={{ width: 60, height: 20 }} />
        <div className="afs-skeleton__bar" style={{ width: 80, height: 20 }} />
      </div>
    </div>
    <div style={{ padding: '0.75rem 1.125rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div className="afs-skeleton__bar" style={{ width: 55, height: 14 }} />
      <div style={{ display: 'flex', gap: '0.375rem' }}>
        <div className="afs-skeleton__bar" style={{ width: 30, height: 30, borderRadius: 6 }} />
        <div className="afs-skeleton__bar" style={{ width: 30, height: 30, borderRadius: 6 }} />
        <div className="afs-skeleton__bar" style={{ width: 30, height: 30, borderRadius: 6 }} />
      </div>
    </div>
  </div>
);

// ===============================================
// EMPTY STATE COMPONENT
// ===============================================

const EmptyState = ({ onCreateNew, isSearch }) => (
  <div className="afs-empty">
    <div className="afs-empty__icon">
      {isSearch ? <IconSearch size={28} /> : <IconRobot size={28} />}
    </div>
    <h3 className="afs-empty__title">
      {isSearch ? 'Nenhum agente encontrado' : 'Nenhum agente criado'}
    </h3>
    <p className="afs-empty__description">
      {isSearch
        ? 'Tente ajustar sua busca para encontrar agentes.'
        : 'Crie seu primeiro agente de IA em poucos minutos. É simples, rápido e não precisa de conhecimento técnico.'}
    </p>
    {!isSearch && (
      <button className="afs-btn-primary" onClick={onCreateNew}>
        <IconPlus size={16} />
        Criar Primeiro Agente
      </button>
    )}
  </div>
);

// ===============================================
// ERROR STATE COMPONENT
// ===============================================

const ErrorState = ({ error, onRetry }) => (
  <div className="afs-error">
    <div className="afs-error__icon">
      <IconAlertTriangle size={28} />
    </div>
    <h3 className="afs-error__title">Erro ao carregar agentes</h3>
    <p className="afs-error__description">{error}</p>
    <button className="afs-btn-primary" onClick={onRetry}>
      <IconRefresh size={16} />
      Tentar novamente
    </button>
  </div>
);

// ===============================================
// AGENT CARD COMPONENT
// ===============================================

const AgentCard = ({ agent, onEdit, onDuplicate, onDelete, onOpenAutomations }) => {
  const capabilities = agent.capabilities || [];

  return (
    <div className="afs-card">
      {/* Header */}
      <div className="afs-card__header">
        <div className="afs-card__avatar">
          <IconRobot size={20} />
        </div>
        <div className="afs-card__info">
          <h3 className="afs-card__name">{agent.name || 'Agente sem nome'}</h3>
          <p className="afs-card__business">{agent.business_info?.name || 'Negócio'}</p>
        </div>
      </div>

      {/* Body - Capabilities */}
      {capabilities.length > 0 && (
        <div className="afs-card__body">
          <div className="afs-card__capabilities">
            {capabilities.slice(0, 4).map((cap) => {
              const code = cap.code || cap;
              const capInfo = CAPABILITIES[code];
              return (
                <span key={code} className="afs-card__tag">
                  {capInfo?.emoji} {capInfo?.shortName || code}
                </span>
              );
            })}
            {capabilities.length > 4 && (
              <span className="afs-card__tag">+{capabilities.length - 4}</span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="afs-card__footer">
        <div className="afs-card__status">
          <div className={`afs-card__status-dot ${agent.is_active ? 'afs-card__status-dot--active' : 'afs-card__status-dot--inactive'}`} />
          <span className={`afs-card__status-text ${agent.is_active ? 'afs-card__status-text--active' : 'afs-card__status-text--inactive'}`}>
            {agent.is_active ? 'Ativo' : 'Inativo'}
          </span>
        </div>

        <div className="afs-card__actions">
          <OverlayTrigger placement="top" overlay={<Tooltip>Automações</Tooltip>}>
            <button className="afs-action-btn afs-action-btn--automations" onClick={() => onOpenAutomations(agent)}>
              <IconBellRinging size={18} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Editar</Tooltip>}>
            <button className="afs-action-btn afs-action-btn--edit" onClick={() => onEdit(agent)}>
              <IconEdit size={18} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Duplicar</Tooltip>}>
            <button className="afs-action-btn afs-action-btn--duplicate" onClick={() => onDuplicate(agent)}>
              <IconCopy size={18} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Excluir</Tooltip>}>
            <button className="afs-action-btn afs-action-btn--danger" onClick={() => onDelete(agent)}>
              <IconTrash size={18} />
            </button>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  );
};

// ===============================================
// MAIN CONTENT COMPONENT
// ===============================================

const ITEMS_PER_PAGE = 10;

const AgentsFlowContent = () => {
  const navigate = useNavigate();
  const { state, setAgents } = useAgentsFlow();
  const { loadAgents, deleteAgent } = useAgentsFlowApi();
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalAgents, setTotalAgents] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { agents, agentsLoading, agentsError } = state;

  // Load first page on mount
  useEffect(() => {
    const loadFirstPage = async () => {
      try {
        const response = await loadAgents({ page: 1, page_size: ITEMS_PER_PAGE });
        setCurrentPage(1);
        setHasMore(!!response?.next);
        setTotalAgents(response?.count || 0);
      } catch {
        // error handled by hook
      }
    };
    loadFirstPage();
  }, [loadAgents]);

  // Load more agents
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await AgentsFlowService.listAgents({ page: nextPage, page_size: ITEMS_PER_PAGE });
      const newAgents = response.data?.results || [];

      if (newAgents.length > 0) {
        setAgents([...agents, ...newAgents]);
        setCurrentPage(nextPage);
        setHasMore(!!response.data?.next);
        setTotalAgents(response.data?.count || totalAgents);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Erro ao carregar mais agentes:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, hasMore, isLoadingMore, agents, setAgents, totalAgents]);

  // Derived data
  const filteredAgents = useMemo(() => {
    if (!searchTerm.trim()) return agents;
    const term = searchTerm.toLowerCase();
    return agents.filter(
      (agent) =>
        agent.name?.toLowerCase().includes(term) ||
        agent.business_info?.name?.toLowerCase().includes(term)
    );
  }, [agents, searchTerm]);

  const stats = useMemo(() => ({
    total: totalAgents || agents.length,
    active: agents.filter((a) => a.is_active).length,
  }), [agents, totalAgents]);

  // Handlers
  const handleCreateNew = useCallback(() => {
    navigate('/agents-flow/canvas');
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await loadAgents({ page: 1, page_size: ITEMS_PER_PAGE });
      setCurrentPage(1);
      setHasMore(!!response?.next);
      setTotalAgents(response?.count || 0);
    } finally {
      setRefreshing(false);
    }
  }, [loadAgents]);

  const handleEdit = useCallback((agent) => {
    navigate(`/agents-flow/canvas/${agent.id}`);
  }, [navigate]);

  const handleOpenAutomations = useCallback((agent) => {
    navigate(`/agents-flow/automations/${agent.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback(async (agent) => {
    const detailsHtml = `Será criada uma cópia completa do agente "<b>${agent.name || 'Agente'}</b>" incluindo configurações, capacidades, fluxo visual, base de conhecimento, integrações e notificações.`
      + '<br><br><span style="color: #d97706; font-size: 13px;">⚠️ Se houver base de conhecimento, ela precisará ser <b>re-treinada</b>.</span>'
      + '<br><span style="color: #64748b; font-size: 13px;">A cópia será criada como <b>inativa</b> e sem número WhatsApp vinculado.</span>';

    const result = await Swal.fire({
      title: 'Duplicar agente?',
      html: detailsHtml,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, duplicar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Duplicando agente...',
        html: 'Copiando configurações, capacidades, fluxo e dados...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const response = await AgentsFlowService.duplicateAgent(agent.id);
        const newAgent = response.data;
        await loadAgents({ page: 1, page_size: ITEMS_PER_PAGE });
        setCurrentPage(1);

        const action = await Swal.fire({
          icon: 'success',
          title: 'Agente duplicado!',
          html: `O agente "<b>${agent.name}</b>" foi duplicado com sucesso como "<b>${newAgent?.name || 'Cópia'}</b>".`,
          showCancelButton: true,
          confirmButtonText: 'Abrir cópia',
          cancelButtonText: 'Ficar aqui',
          confirmButtonColor: '#3b82f6',
          cancelButtonColor: '#6b7280',
        });

        if (action.isConfirmed && newAgent?.id) {
          navigate(`/agents-flow/canvas/${newAgent.id}`);
        }
      } catch (error) {
        console.error('Erro ao duplicar agente:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro ao duplicar',
          text: error?.response?.data?.error || 'Não foi possível duplicar o agente. Tente novamente.',
        });
      }
    }
  }, [loadAgents, navigate]);

  const handleDelete = useCallback(async (agent) => {
    const result = await Swal.fire({
      title: 'Excluir agente?',
      html: `Tem certeza que deseja excluir "<b>${agent.name || 'Agente'}</b>"?<br><br><span style="color: #64748b; font-size: 14px;">Todos os dados relacionados serão removidos. Esta ação não pode ser desfeita.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Excluindo agente...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        await deleteAgent(agent.id);
        Swal.fire({
          icon: 'success',
          title: 'Agente excluído!',
          text: 'O agente foi excluído com sucesso.',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Erro ao excluir agente:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível excluir o agente. Tente novamente.',
        });
      }
    }
  }, [deleteAgent]);

  // Error state
  if (agentsError && !agentsLoading) {
    return <ErrorState error={agentsError} onRetry={handleRefresh} />;
  }

  return (
    <>
      {/* Header */}
      <div className="afs-header">
        <div className="afs-header__left">
          <div className="afs-header__icon">
            <IconRobot size={22} />
          </div>
          <div>
            <h2 className="afs-header__title">Meus Agentes</h2>
            <p className="afs-header__subtitle">Gerencie seus agentes de IA</p>
          </div>
        </div>
        <div className="afs-header__actions">
          <button
            className="afs-btn-secondary"
            onClick={handleRefresh}
            disabled={refreshing || agentsLoading}
          >
            <IconRefresh size={16} className={refreshing ? 'afs-spin' : ''} />
          </button>
          <button className="afs-btn-primary" onClick={handleCreateNew}>
            <IconPlus size={16} />
            Novo Agente
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="afs-search">
        <div className="afs-search__icon">
          <IconSearch size={18} />
        </div>
        <input
          type="text"
          className="afs-search__input"
          placeholder="Buscar agentes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Stats */}
      <div className="afs-stats">
        <div className="afs-stats__card">
          <div className="afs-stats__value">{stats.total}</div>
          <div className="afs-stats__label">Total</div>
        </div>
        <div className="afs-stats__card">
          <div className="afs-stats__value">{stats.active}</div>
          <div className="afs-stats__label">Ativos</div>
        </div>
      </div>

      {/* Content */}
      {agentsLoading && agents.length === 0 ? (
        <div className="afs-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <AgentCardSkeleton key={i} />
          ))}
        </div>
      ) : filteredAgents.length === 0 && searchTerm ? (
        <EmptyState isSearch onCreateNew={handleCreateNew} />
      ) : agents.length === 0 ? (
        <EmptyState onCreateNew={handleCreateNew} />
      ) : (
        <>
          <div className="afs-grid">
            {filteredAgents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                onOpenAutomations={handleOpenAutomations}
              />
            ))}
          </div>

          {/* Load More */}
          {hasMore && !searchTerm && (
            <div className="afs-load-more">
              <button
                className="afs-btn-secondary"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <IconRefresh size={16} className="afs-spin" />
                    Carregando...
                  </>
                ) : (
                  `Carregar mais agentes (${agents.length} de ${totalAgents})`
                )}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

// ===============================================
// MAIN SCREEN COMPONENT
// ===============================================

const AgentsFlowScreen = () => {
  return (
    <AgentsFlowProvider>
      <div className="agents-flow-container">
        <div className="agents-list-container">
          <AgentsFlowContent />
        </div>
      </div>
    </AgentsFlowProvider>
  );
};

export default AgentsFlowScreen;
